/**
 * Plugins Routes (Hono + OpenAPI)
 *
 * GET /:code/image — serves plugin image file with lazy-cached map.
 */

import { OpenAPIHono, createRoute, z } from '@hono/zod-openapi'
import { existsSync, readFileSync } from 'node:fs'
import type { Context } from 'hono'
import { type ModuleContext, mimeFromFilename } from '@gzl10/nexus-sdk'
import type { NexusEnv, OpenApiHandler } from '../../http/types.js'

type C = Context<NexusEnv>

export function createPluginRoutes(ctx: ModuleContext) {
  const app = new OpenAPIHono<NexusEnv>()

  // Lazy cache: built once on first request, avoids re-scanning on every image load
  let imageMap: Map<string, string> | null = null

  async function getImageMap(): Promise<Map<string, string>> {
    if (!imageMap) {
      const discovered = await ctx.core.plugins.discover()
      imageMap = new Map(
        discovered
          .filter(p => p.image && existsSync(p.image))
          .map(p => [p.code, p.image!])
      )
    }
    return imageMap
  }

  const anyObject = z.object({}).passthrough()

  const imageRoute = createRoute({
    method: 'get',
    path: '/{code}/image',
    tags: ['Plugins'],
    summary: 'Serve the plugin image file',
    request: {
      params: z.object({ code: z.string().regex(/^[a-z]{3}$/) })
    },
    responses: {
      200: {
        description: 'Plugin image binary',
        content: { 'image/*': { schema: z.string() } }
      },
      400: { description: 'Invalid plugin code', content: { 'application/json': { schema: anyObject } } },
      404: { description: 'Plugin image not found', content: { 'application/json': { schema: anyObject } } }
    }
  })

  app.openapi(imageRoute, (async (c: C): Promise<Response> => {
    const code = c.req.param('code')!

    if (!/^[a-z]{3}$/.test(code)) {
      return c.json({ error: { code: 'INVALID_CODE', message: 'Invalid plugin code' } }, 400)
    }

    const map = await getImageMap()
    const imagePath = map.get(code)

    if (!imagePath) {
      return c.json({ error: { code: 'NOT_FOUND', message: 'Plugin image not found' } }, 404)
    }

    const buf = readFileSync(imagePath)
    const mime = mimeFromFilename(imagePath)
    c.header('Content-Type', mime)
    c.header('Cache-Control', 'public, max-age=86400, immutable')
    return c.body(new Uint8Array(buf))
  }) as OpenApiHandler<NexusEnv>)

  return app
}
